import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { AIAssistant } from "../Dashboard/AIAssistant";
import { UserProfile } from "@shared/types";

interface MainLayoutProps {
  children: React.ReactNode;
  user: UserProfile;
  onLogout: () => void;
}

export function MainLayout({ children, user, onLogout }: MainLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [path] = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setSidebarOpen(false);
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      mainRef.current.scrollLeft = 0;
    }
  }, [path]);

  const toggleSidebar = () => {
    if (window.matchMedia?.("(min-width: 1024px)").matches) {
      setDesktopCollapsed(value => !value);
    } else {
      setSidebarOpen(value => !value);
    }
  };

  // Handle clicks outside the sidebar on mobile to close it
  const handleMainContentClick = () => {
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar 
        user={user} 
        isOpen={sidebarOpen}
        desktopCollapsed={desktopCollapsed}
        onClose={() => setSidebarOpen(false)} 
        onLogout={onLogout}
      />

      {/* Main Content */}
      <div className={`min-w-0 flex-1 flex flex-col ${desktopCollapsed ? "" : "lg:pl-64"}`} onClick={handleMainContentClick}>
        <Header 
          user={user} 
          onSidebarToggle={toggleSidebar} 
          onLogout={onLogout} 
        />

        <main ref={mainRef} className="flex-1 overflow-y-auto bg-gray-50 dark:bg-neutral-900 p-4 md:p-6 pb-24">
          {children}
        </main>

        {/* AI Assistant Bar */}
        <div className={sidebarOpen ? "hidden lg:block" : ""}><AIAssistant sidebarExpanded={!desktopCollapsed} /></div>
      </div>
    </div>
  );
}
